import classNames from "classnames";
import React, { useState } from "react";
import ReactDOM from "react-dom";
import styled, { css } from "styled-components";
import { Icon } from "./Icon";
import { Tooltip } from "./Tooltip";
import { style } from "./style";
import { focusOutline } from "./utils/focusOutlineStyle";

export enum ColorTheme {
  DARK = "dark",
  LIGHT = "light",
}

export type Props = {
  children: React.ReactNode;
  language?: string;
  className?: string;
  lineNumbers?: boolean;
  start?: number;
  copy?: string;
  commandLine?: boolean;
  prompt?: string;
  skipPrompt?: string;
  prefix?: string;
  line?: string;
  title?: string;
  titleAlign?: string;
  download?: string;
  downloadLinkTitle?: string;
  noToolbar?: boolean;
  theme?: ColorTheme;
  callback?: () => void;
};

// ms to show copied state
const COPIED_DURATION = 2000;

// default icon to show
const ICON_DEFAULT = "copy";
// icon to show when showing copied state
const ICON_COPIED = "check";

const baseCodeSyntaxStyles = css`
  code[class*="language-"],
  pre[class*="language-"] {
    position: relative;
    color: ${style.colors.grey.lightest};
    background: none;
    text-shadow: 0 1px rgba(0, 0, 0, 0.3);
    font-family: ${style.vars.monoFont};
    text-align: left;
    white-space: pre;
    word-spacing: normal;
    word-break: normal;
    word-wrap: normal;
    line-height: 1.5;
    tab-size: 4;
    hyphens: none;
  }

  /* Code blocks */
  pre[class*="language-"] {
    padding: ${style.vars.space["3"]};
    margin-bottom: 0;
    overflow: auto;
    border: none;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    font-weight: ${style.vars.fontWeight.bold};
    z-index: ${style.vars.zIndex.base};

    &.no-toolbar {
      border-radius: ${style.vars.borderRadius.base};
    }
  }

  :not(pre) > code[class*="language-"],
  pre[class*="language-"] {
    background: ${style.colors.cli.background};
  }

  /* Single line prefix */
  pre[class*="language-"] > code[data-prefix]:before {
    color: ${style.colors.shadesOfGrey["8"]};
    content: attr(data-prefix);
    padding-right: ${style.vars.space["2"]};
  }

  /* Inline code */
  :not(pre) > code[class*="language-"] {
    padding: 0.1em;
    border-radius: 0.3em;
    white-space: normal;
  }

  .token.comment,
  .token.prolog,
  .token.doctype,
  .token.cdata {
    color: ${style.colors.grey.dark};
  }

  .namespace {
    opacity: 0.7;
  }

  .token.property,
  .token.tag,
  .token.constant,
  .token.symbol,
  .token.deleted {
    color: ${style.colors.cli.green};
  }

  .token.boolean,
  .token.number {
    color: ${style.colors.cli.orange};
  }

  .token.selector,
  .token.attr-name,
  .token.string,
  .token.char,
  .token.builtin,
  .token.inserted {
    color: ${style.colors.cli.green};
  }

  .token.operator,
  .token.entity,
  .token.url,
  .token.punctuation,
  .language-css .token.string,
  .style .token.string,
  .token.variable {
    color: ${style.colors.grey.lightest};
  }

  .token.atrule,
  .token.attr-value,
  .token.function,
  .token.class-name {
    color: ${style.colors.cli.blue};
  }

  .token.keyword {
    color: ${style.colors.cli.purple};
  }

  .token.regex,
  .token.important {
    color: ${style.colors.cli.purple};
  }

  .token.important,
  .token.bold {
    font-weight: ${style.vars.fontWeight.bold};
  }

  .token.italic {
    font-style: italic;
  }

  .token.entity {
    cursor: help;
  }

  pre[class*="language-"].line-numbers {
    position: relative;
    padding-left: 2.75rem;
    counter-reset: linenumber;
  }

  pre[class*="language-"].line-numbers > code {
    z-index: ${style.vars.zIndex.base};
  }

  .line-numbers .line-numbers-rows {
    position: absolute;
    pointer-events: none;
    top: 0;
    font-size: 100%;
    left: -${style.vars.space["7"]};
    width: ${style.vars.space["7"]}; /* works for line-numbers below 1000 lines */
    letter-spacing: -1px;
    user-select: none;
  }

  .line-numbers-rows > span {
    pointer-events: none;
    display: block;
    counter-increment: linenumber;
  }

  .line-numbers-rows > span:before {
    content: counter(linenumber);
    color: ${style.colors.grey.primary};
    display: block;
    padding-right: ${style.vars.space["3"]};
    text-align: right;
  }

  .line-highlight {
    position: absolute;
    margin-top: -1px;
    left: -${style.vars.space["3"]};
    right: -100%;
    background-color: ${style.colors.cli.highlight};
    pointer-events: none;
    line-height: inherit;
    white-space: pre;
    z-index: ${style.vars.zIndex.negative};

    &:before {
      content: "";
      position: absolute;
      background-color: ${style.colors.grey.primary};
      left: 0;
      top: 0;
      bottom: 0;
      width: ${style.vars.space["2"]};
    }
  }

  .line-numbers .line-highlight {
    margin-top: 1.2em;
    left: 0;
    right: 0;
    z-index: initial;

    &:before {
      bottom: initial;
    }
  }

  .line-numbers .line-highlight:before,
  .line-numbers .line-highlight[data-end]:after {
    content: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggc3Ryb2tlPSIjODM5MEFGIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgZD0iTTQgOGg3Ljk5NyIvPjxwYXRoIGQ9Ik0xMC4wODIgNUwxMyA3LjkxOGwtMi45MTggMi45MTgiIHN0cm9rZT0iIzgzOTBBRiIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz48L3N2Zz4=");
    position: absolute;
    top: 0;
    left: ${style.vars.space["2"]};
    padding-left: ${style.vars.space["2"]};
    width: ${style.vars.space["5"]};
    font-size: 17px;
    background-color: ${style.colors.cli.highlight};
    z-index: ${style.vars.zIndex.base};
  }

  .command-line-prompt {
    border-right: 1px solid ${style.colors.grey.dark};
    display: block;
    float: left;
    font-size: 100%;
    letter-spacing: -1px;
    margin-right: 1em;
    pointer-events: none;

    user-select: none;
  }

  .command-line-prompt > span:before {
    color: ${style.colors.grey.dark};
    content: " ";
    display: block;
    padding-right: 0.8em;
  }

  .command-line-prompt > span[data-user]:before {
    content: "[" attr(data-user) "@" attr(data-host) "] $";
  }

  .command-line-prompt > span[data-user="root"]:before {
    content: "[" attr(data-user) "@" attr(data-host) "] #";
  }

  .command-line-prompt > span[data-prompt]:before {
    content: attr(data-prompt);
  }

  /* code block title */
  pre[class*="code-block-title"][data-title]:before {
    width: 100%;
    content: attr(data-title);
    display: flex;
    align-items: center;
    padding-bottom: ${style.vars.space["2"]};
    margin-bottom: ${style.vars.space["3"]};
    margin-top: -${style.vars.space["2"]};
  }

  pre[class*="code-block-title"][data-title]:after {
    position: absolute;
    content: "";
    height: 1px;
    left: 0;
    right: 0;
    background-color: ${style.colors.grey.dark};
    top: 37px;
  }

  pre[class*="code-block-title"][class*="line-numbers"][data-title]:before {
    margin-left: -24px;
  }

  pre[class*="line-numbers"][class*="code-block-title"][data-title] .line-highlight {
    margin-top: 52px;
  }

  pre[class*="code-block-title"][class*="code-block-title-center"]:before {
    justify-content: center;
  }

  pre[class*="code-block-title"][class*="code-block-title-right"]:before {
    justify-content: flex-end;
  }

  .toolbar {
    display: flex;
    background-color: transparent;
    padding: ${style.vars.space["2"]};
    border-bottom-left-radius: 3px;
    border-bottom-right-radius: 3px;
    border-top: 1px solid ${style.colors.grey.primary};
    z-index: 1;
  }

  .toolbar-item {
    height: 27px;
    &:not(:only-child):first-child {
      min-width: 87px;
    }
  }

  pre.diff-highlight > code .token.deleted:not(.prefix),
  pre > code.diff-highlight .token.deleted:not(.prefix) {
    background-color: rgba(255, 0, 0, 0.1);
    color: inherit;
    display: block;
  }

  pre.diff-highlight > code .token.inserted:not(.prefix),
  pre > code.diff-highlight .token.inserted:not(.prefix) {
    background-color: rgba(0, 255, 128, 0.1);
    color: inherit;
    display: block;
  }
`;

const Wrapper = styled.div<{ singleLine: boolean; theme: ColorTheme }>`
  ${baseCodeSyntaxStyles};

  &:focus {
    ${focusOutline()};
  }

  ${({ theme }) =>
    theme &&
    (theme === ColorTheme.LIGHT
      ? css`
          .toolbar {
            background: ${style.colors.grey.lightest};
            border-top: 1px solid ${style.colors.grey.primary};
          }
          code[class*="language-"],
          pre[class*="language-"] {
            background-color: ${style.colors.grey.lightest};
            color: ${style.colors.grey.darkest};
            text-shadow: none;
            font-weight: ${style.vars.fontWeight.light};
          }
          .token.operator,
          .token.entity,
          .token.url,
          .token.punctuation,
          .language-css .token.string,
          .style .token.string,
          .token.variable {
            color: ${style.colors.grey.darkest};
          }
        `
      : css`
          .toolbar {
            background: ${style.colors.cli.background};
          }
          pre[class*="language-"] {
            background-color: ${style.colors.cli.background};
          }
        `)}

  ${({ singleLine }) =>
    singleLine &&
    css`
      .code-toolbar {
        display: flex;
      }

      pre[class*="language-"] {
        border-top-right-radius: 0;
        border-bottom-left-radius: ${style.vars.borderRadius.base};
      }

      .toolbar {
        padding: 0;
        border-bottom-left-radius: 0;
        border-top-right-radius: ${style.vars.borderRadius.base};
        border-left: 1px solid ${style.colors.grey.dark};
        border-top: 0;
      }

      .toolbar-item {
        margin-right: 0;
        height: 100%;
      }
    `}
`;

const ToolbarButtonWrapper = styled.button<{
  singleLine: boolean;
  theme: string;
}>`
  display: flex;
  align-items: center;
  padding: ${style.vars.space["1"]} ${style.vars.space["2"]};
  border-radius: ${style.vars.borderRadius.base};
  border: none;
  color: ${style.colors.grey.light};
  font-size: ${style.vars.fontSize.small};
  font-weight: ${style.vars.fontWeight.bold};
  cursor: pointer;

  &:hover {
    color: ${style.colors.cli.background};
    background-color: ${style.colors.grey.light};
  }

  ${({ theme }) =>
    theme &&
    (theme === ColorTheme.LIGHT
      ? css`
          background-color: ${style.colors.grey.lightest};
          color: ${style.colors.grey.dark};
          &:hover {
            color: ${style.colors.white};
            background-color: ${style.colors.grey.dark};
          }
        `
      : css`
          background-color: ${style.colors.cli.background};
          color: ${style.colors.grey.light};
        `)}

  ${({ singleLine }) =>
    singleLine &&
    css`
      width: ${style.vars.space["7"]};
      justify-content: center;
      height: 100%;
      padding: 0;
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    `}

  &:focus {
    ${focusOutline()};
  }
`;

const TooltipSpan = styled.span`
  display: flex;
  height: 100%;
  width: 100%;
  align-items: center;
  justify-content: center;

  &:focus {
    ${focusOutline()};
  }
`;

const PrismIcon = styled(Icon)<{ singleLine?: boolean; isHovered?: boolean }>`
  margin-right: ${({ singleLine }) => (singleLine ? 0 : style.vars.space["2"])};
`;

type ToolbarButtonProps = {
  text: string;
  icon: string;
  singleLine?: boolean;
  ariaLabel?: string;
  theme?: ColorTheme;
  // eslint-disable-next-line @typescript-eslint/ban-types
  onClick?: (event: object) => void;
};

const ToolbarButton: React.FC<ToolbarButtonProps> = ({ text, icon, singleLine = false, ariaLabel, theme = ColorTheme.DARK, onClick }) => {
  return (
    <ToolbarButtonWrapper
      onClick={onClick}
      singleLine={singleLine}
      theme={theme}
      tabIndex={!singleLine ? 0 : undefined}
      aria-label={ariaLabel}
    >
      {singleLine ? (
        <Tooltip content={text}>
          <TooltipSpan>
            <PrismIcon singleLine icon={icon} />
          </TooltipSpan>
        </Tooltip>
      ) : (
        <>
          <PrismIcon icon={icon} />
          <span>{text}</span>
        </>
      )}
    </ToolbarButtonWrapper>
  );
};

export const Code: React.FC<Props> = (props) => {
  const codeRef = React.useRef<HTMLElement>(null);
  const language = props.language || "text";
  const [isSingleLine, setIsSingleLine] = React.useState(false);
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const [copied, setIsCopied] = useState(false);
  const [btnIcon, setBtnIcon] = useState(ICON_DEFAULT);

  // Although we're trying to get away from using class names, this is required to work with prism.js
  const classes = classNames({
    [`language-${language}`]: true,
    "line-numbers": props.lineNumbers,
    "command-line": props.commandLine,
    "code-block-title": props.title,
    [`code-block-title-${props.titleAlign}`]: props.title && props.titleAlign,
    "no-toolbar": props.noToolbar,
  });

  React.useEffect(() => {
    if ((window as any).Prism) {
      if (codeRef.current) {
        (window as any).Prism.highlightElement(codeRef.current);
      }

      if (props.noToolbar) {
        const code = codeRef.current?.parentElement?.parentElement; // .code-toolbar
        const toolbar = code?.querySelector(".toolbar");

        if (toolbar) {
          code?.removeChild(toolbar);
        }
      } else {
        // Replace prism copy and download buttons with our own components for more control of styles
        const toolbarItems = codeRef.current?.parentElement?.parentElement?.querySelectorAll(".toolbar-item");

        if (toolbarItems && toolbarItems.length > 0) {
          const prismCopyButton = toolbarItems[0].getElementsByTagName("a")[0];

          // Remove tab index so we can use our own focus styles on ToolbarButton
          // Otherwise we'll have to tab twice — once on the <a> with the default outline and then ToolbarButton
          prismCopyButton.tabIndex = -1;

          ReactDOM.render(
            <ToolbarButton
              text={props.copy ? props.copy : copied ? "Copied" : "Copy"}
              icon={btnIcon}
              theme={props.theme}
              singleLine={isSingleLine}
              ariaLabel="copy to clipboard"
              onClick={() => {
                if (!copied) {
                  setIsCopied(true);
                  setBtnIcon(ICON_COPIED);
                  const timer = setTimeout(() => {
                    setBtnIcon(ICON_DEFAULT);
                    setIsCopied(false);
                    clearTimeout(timer);
                  }, COPIED_DURATION);
                }
                if (props.callback) {
                  props.callback();
                }
              }}
            />,
            prismCopyButton
          );
        }

        if (props.download && toolbarItems && toolbarItems.length > 1) {
          const prismDownloadButton = toolbarItems[1].getElementsByTagName("a")[0];

          prismDownloadButton.tabIndex = -1;

          ReactDOM.render(
            <ToolbarButton
              text={props.downloadLinkTitle || "Download"}
              icon="download"
              ariaLabel="download"
              theme={props.theme}
              onClick={props.callback}
            />,
            prismDownloadButton
          );
        }
      }
    }

    setIsSingleLine(
      props.children !== undefined &&
        props.children !== null &&
        React.Children.count(props.children) === 1 &&
        props.children.toString().split(/\s{3,}|\r\n|^/gm).length === 1
    );
  }, [codeRef.current, props, btnIcon, copied]);

  return (
    <Wrapper singleLine={isSingleLine} theme={props.theme} role="figure" aria-label="code snippet" tabIndex={0}>
      <pre
        className={classes}
        data-start={props.start}
        data-prompt={props.prompt}
        data-output={props.skipPrompt}
        data-line={props.line}
        data-title={props.title}
        data-src={props.download}
        data-download-link={props.download}
        tabIndex={0} // eslint-disable-line jsx-a11y/no-noninteractive-tabindex
      >
        <code ref={codeRef} data-copy={props.copy} data-prefix={props.prefix}>
          {props.children}
        </code>
      </pre>
    </Wrapper>
  );
};
